import { z } from 'zod';
import { type Metric, metricsOf } from './resultRows.js';
import { num, text } from './resultValues.js';

export const elementListSchema = z.object({
  gems: z.array(
    z.looseObject({
      feature: z.string().refine((value) => value.trim().length > 0, 'Feature must not be blank'),
      tag: z.string().refine((value) => value.trim().length > 0, 'Tag must not be blank'),
    }),
  ),
});
// The service's own row key: a gem is one tag of one feature, in one scene, on
// one network — so nothing shorter identifies it.
const KEY_FIELDS = ['feature', 'scene', 'tag', 'network'] as const;

export type ElementList = z.infer<typeof elementListSchema>;

export interface Gem {
  key: string;
  feature: string;
  tag: string;
  scene: string | null;
  network: string | null;
  creativeCount: number;
  potential: string | null;
  spendShare: number | null;
  metrics: Metric[];
}

export interface GemGroup {
  category: string;
  // The service counts and ranks the category, rather than the product deriving
  // it from the page: a run capped at 20 rows has not seen the whole category.
  gemCount: number | null;
  bestUplift: number | null;
  gems: Gem[];
}

export function gemKeyOf(gem: ElementList['gems'][number]): string {
  return KEY_FIELDS.map((field) => text(gem[field]) ?? '').join('|');
}

function gemOf(gem: ElementList['gems'][number]): Gem {
  const creatives = gem['creatives'];
  return {
    key: gemKeyOf(gem),
    feature: gem.feature,
    tag: gem.tag,
    scene: text(gem['scene']),
    network: text(gem['network']),
    creativeCount: Array.isArray(creatives) ? creatives.length : 0,
    potential: text(gem['potential']),
    spendShare: num(gem['spend_share']),
    metrics: metricsOf(gem),
  };
}

export function gemGroupsOf(result: ElementList): GemGroup[] {
  const groups = new Map<string, GemGroup>();
  for (const record of result.gems) {
    const gem = gemOf(record);
    const category = text(record['category']) ?? 'Uncategorised';
    const group = groups.get(category);
    if (group === undefined) {
      groups.set(category, {
        category,
        gemCount: num(record['category_gem_count']),
        bestUplift: num(record['category_best_uplift']),
        gems: [gem],
      });
      continue;
    }
    group.gems.push(gem);
  }
  return [...groups.values()];
}

export function gemKeysOf(groups: GemGroup[]): string[] {
  return groups.flatMap((group) => group.gems.map((gem) => gem.key));
}
